import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Form, Select } from 'antd';

import {
  AUTO_COLLECTED_ID,
  collectedDataMap,
  INTEGRATIONS_ID,
} from '../../../forms/forms.constants';

import { getPopupContainer } from '../pdfHelpers';

const tableFields = { table: true, weather: true, timeTrackingGrid: true };

const REPEATABLE_SECTION_TITLE_FIELD = {
  id: 'repeatableSectionTitle',
  configProps: { title: 'Section Title' },
};

const collectedToField = ({ id, title }) => ({
  id,
  configProps: { title },
});

export default function PDFDesignerLinkPicker({
  link: {
    sectionId,
    fieldId,
    collectedId,
  } = {},
  style = {},
  sections = [],
  collected = {},
  onLinkChanged,
  isTable,
  disabled = false,
  lockSectionId,
}) {
  const {
    connectedToDocusign,
  } = useSelector((state) => state.settings.company);

  const lockedSection = useMemo(() => (
    lockSectionId ? sections.find((s) => s.id === lockSectionId) : null
  ), [sections, lockSectionId]);

  const ourFields = useMemo(() => {
    if (lockSectionId) {
      if (!lockedSection) return [];
      const { fields = [] } = lockedSection;
      const sectionFields = fields?.filter(({ selectedType }) => (
        isTable ? tableFields[selectedType] : !tableFields[selectedType]
      ));
      if (!isTable) sectionFields.unshift(REPEATABLE_SECTION_TITLE_FIELD);
      return sectionFields;
    }
    if (sectionId === AUTO_COLLECTED_ID || collectedId) {
      const collectedFields = [
        collectedToField(collectedDataMap.date),
        collectedToField(collectedDataMap.employeeName),
        collectedToField(collectedDataMap.employeeId),
      ];
      Object.keys(collected).forEach((key) => {
        const { collect } = collected[key];
        if (!collect || !(key in collectedDataMap)) return;
        collectedFields.push(collectedToField(collectedDataMap[key]));
      });
      return collectedFields;
    }
    if (sectionId === INTEGRATIONS_ID) {
      // Just external signatures for now
      return [
        {
          id: 'docusignSignatures',
          configProps: { title: 'External Signatures' },
        },
      ];
    }

    const chosenSection = sections.find((section) => section.id === sectionId);
    if (!chosenSection) return [];
    const {
      fields = [],
    } = chosenSection;
    return fields.filter(({ selectedType }) => (
      isTable ? tableFields[selectedType] : !tableFields[selectedType]
    ));
  }, [sections, sectionId, collected, collectedId, isTable, lockedSection]);

  const nonRepeatableSections = useMemo(() => (
    sections.filter(({ settings: { isDuplicationEnabled = false } = {} }) => !isDuplicationEnabled)
  ), [sections]);

  const ourSectionId = collectedId ? AUTO_COLLECTED_ID : (lockSectionId || sectionId);
  const ourFieldId = collectedId || fieldId;

  return (
    <>
      <Form.Item
        key="section"
        label="Section"
        style={style}
      >
        <Select
          value={lockSectionId || ourSectionId}
          onChange={(newSectionId) => onLinkChanged({ sectionId: newSectionId })}
          allowClear
          disabled={disabled || lockSectionId}
          getPopupContainer={getPopupContainer}
        >
          {!isTable && (
            <Select.Option value={AUTO_COLLECTED_ID} key={AUTO_COLLECTED_ID}>
              Automatically Collected
            </Select.Option>
          )}
          {
            connectedToDocusign && (
              <Select.Option value={INTEGRATIONS_ID} key={INTEGRATIONS_ID}>
                Integrations
              </Select.Option>
            )
          }
          {
            nonRepeatableSections.map(({
              id,
              name,
            }) => (
              <Select.Option value={id} label={name} key={id}>
                {name}
              </Select.Option>
            ))
          }
          {lockSectionId && (
            <Select.Option value={lockSectionId} key={lockSectionId}>
              {lockedSection?.name || 'Section'}
            </Select.Option>
          )}
        </Select>
      </Form.Item>
      <Form.Item
        key="field"
        label="Field"
        style={style}
      >
        <Select
          value={ourFieldId}
          disabled={disabled || ourFields.length === 0}
          onChange={(newFieldId) => onLinkChanged(
            lockSectionId
              ? { sectionId: lockSectionId, fieldId: newFieldId }
              : { fieldId: newFieldId },
          )}
          allowClear
          getPopupContainer={getPopupContainer}
        >
          {
            ourFields.map(({
              id, configProps: { title } = {},
            }) => (
              <Select.Option value={id} label={title} key={id}>
                {title}
              </Select.Option>
            ))
          }
        </Select>
      </Form.Item>
    </>
  );
}
